// @section: app-router
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { lazy, Suspense, useEffect, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AuthGuard from "@/components/AuthGuard";
import ErrorBoundary from "@/components/ErrorBoundary";
import Layout from "@/components/Layout";

const queryClient = new QueryClient();
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const AIGrowthAcademy = lazy(() => import("@/pages/AIGrowthAcademy"));
const AIGrowthArticlePage = lazy(() => import("@/pages/AIGrowthArticlePage"));
const AIAssistantLandingPage = lazy(() => import("@/pages/AIAssistantLandingPage"));
const ProductMatrix = lazy(() => import("@/pages/ProductMatrix"));
const SolutionsPage = lazy(() => import("@/pages/SolutionsPage"));
const SolutionDetailPage = lazy(() => import("@/pages/SolutionDetailPage"));
const CustomerCasesPage = lazy(() => import("@/pages/CustomerCasesPage"));
const CustomerCaseDetailPage = lazy(() => import("@/pages/CustomerCaseDetailPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Workbench = lazy(() => import("@/pages/Workbench"));
const SEODiagnosis = lazy(() => import("@/pages/SEODiagnosis"));
const SiteGroup = lazy(() => import("@/pages/SiteGroup"));
const SiteSettings = lazy(() => import("@/pages/SiteSettings"));
const AIWorkbench = lazy(() => import("@/pages/AIWorkbench"));
const AnalyticsCenter = lazy(() => import("@/pages/AnalyticsCenter"));
const ReportCenter = lazy(() => import("@/pages/ReportCenter"));
const GEOCenter = lazy(() => import("@/pages/GEOCenter"));
const GEODiagnosis = lazy(() => import("@/pages/GEODiagnosis"));
const ResourceManagement = lazy(() => import("@/pages/ResourceManagement"));
const KeywordCenter = lazy(() => import("@/pages/KeywordCenter"));
const AIContentFactory = lazy(() => import("@/pages/AIContentFactory"));
const APICenter = lazy(() => import("@/pages/APICenter"));
const SiteBuilder = lazy(() => import("@/pages/SiteBuilder"));
const SiteBuildPipeline = lazy(() => import("@/pages/SiteBuildPipeline"));
const TemplateDemo = lazy(() => import("@/pages/TemplateDemo"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PublicGeoReportPage = lazy(() => import("@/pages/PublicGeoReportPage"));
const SystemSettings = lazy(() => import("@/pages/SystemSettings"));
const Help = lazy(() => import("@/pages/Help"));
const MarketingTools = lazy(() => import("@/pages/MarketingTools"));
const AIDesignCenter = lazy(() => import("@/pages/AIDesignCenter"));
const AIPromptCenter = lazy(() => import("@/pages/AIPromptCenter"));
const CustomerManagement = lazy(() => import("@/pages/CustomerManagement"));
const MediaSessionCenter = lazy(() => import("@/pages/MediaSessionCenter"));
const TeamCollaboration = lazy(() => import("@/pages/TeamCollaboration"));
const AgentManagement = lazy(() => import("@/pages/AgentManagement"));
const ProjectManagement = lazy(() => import("@/pages/ProjectManagement"));

function RouteLoading({ label = "页面加载中..." }: { label?: string }) {
  return (
    <div className="flex min-h-[320px] items-center justify-center">
      <div className="rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-600 shadow-sm">
        {label}
      </div>
    </div>
  );
}

function AdminOnlyRoute({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/workbench" replace />;
  return <>{children}</>;
}

function PricingRoute() {
  const { user, loading } = useAuth();
  if (loading) return <RouteLoading />;
  if (user) return <Layout><Pricing variant="admin" /></Layout>;
  return <Pricing variant="public" />;
}

function LegacyHashRedirect() {
  const navigate = useNavigate();

  useEffect(() => {
    const legacyPath = window.location.hash.startsWith("#/") ? window.location.hash.slice(1) : "";
    if (legacyPath) navigate(legacyPath, { replace: true });
  }, [navigate]);

  return null;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Sonner />
          <BrowserRouter>
            <LegacyHashRedirect />
            <Suspense fallback={<RouteLoading />}>
              <Routes>
            {/* 公开路由：不需要 Layout 和 AuthGuard */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/product-matrix" element={<ProductMatrix />} />
            <Route path="/solutions" element={<SolutionsPage />} />
            <Route path="/solutions/:slug" element={<SolutionDetailPage />} />
            <Route path="/customer-cases" element={<CustomerCasesPage />} />
            <Route path="/customer-cases/:slug" element={<CustomerCaseDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/ai-growth-academy" element={<AIGrowthAcademy />} />
            <Route path="/ai-growth-academy/:slug" element={<AIGrowthArticlePage />} />
            <Route path="/ai-assistant" element={<AIAssistantLandingPage />} />
            <Route path="/pricing" element={<PricingRoute />} />
            <Route path="/geo-reports" element={<PublicGeoReportPage />} />
            <Route path="/public/geo-report/:reportId" element={<PublicGeoReportPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/template-demo/:assetKey/:page" element={
              <AuthGuard>
                <TemplateDemo />
              </AuthGuard>
            } />

            {/* 受保护路由：需要 AuthGuard + Layout */}
            <Route path="/dashboard" element={
              <AuthGuard>
                <Layout><Dashboard /></Layout>
              </AuthGuard>
            } />
            <Route path="/workbench" element={
              <AuthGuard>
                <Layout><Workbench /></Layout>
              </AuthGuard>
            } />
            <Route path="/seo-diagnosis" element={
              <AuthGuard>
                <Layout><SEODiagnosis /></Layout>
              </AuthGuard>
            } />
            <Route path="/sites" element={
              <AuthGuard>
                <Layout>
                  <Suspense fallback={<RouteLoading label="站群管理加载中..." />}>
                    <SiteGroup />
                  </Suspense>
                </Layout>
              </AuthGuard>
            } />
            <Route path="/resources" element={
              <AuthGuard>
                <Layout><ResourceManagement /></Layout>
              </AuthGuard>
            } />
            <Route path="/sites/:siteId/settings" element={
              <AuthGuard>
                <Layout><SiteSettings /></Layout>
              </AuthGuard>
            } />
            <Route path="/site-builder" element={
              <AuthGuard>
                <Layout><SiteBuilder /></Layout>
              </AuthGuard>
            } />
            <Route path="/site-build-pipeline" element={
              <AuthGuard>
                <Layout><SiteBuildPipeline /></Layout>
              </AuthGuard>
            } />
            <Route path="/ai" element={
              <AuthGuard>
                <Layout><AIWorkbench /></Layout>
              </AuthGuard>
            } />
            <Route path="/workflows" element={
              <AuthGuard>
                <Layout><AIWorkbench /></Layout>
              </AuthGuard>
            } />
            <Route path="/team-collaboration/*" element={
              <AuthGuard>
                <Layout><TeamCollaboration /></Layout>
              </AuthGuard>
            } />
            <Route path="/content-factory" element={
              <AuthGuard>
                <Layout><AIContentFactory /></Layout>
              </AuthGuard>
            } />
            <Route path="/analytics" element={
              <AuthGuard>
                <Layout><AnalyticsCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/reports" element={
              <AuthGuard>
                <Layout><ReportCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-center" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-diagnosis" element={
              <AuthGuard>
                <Layout><GEODiagnosis /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-prompts" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-ai-answers" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-map-annotation" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-sources" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-competitors" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-content-gap" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-content-optimization" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-tasks" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-tracking" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-iteration" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-assets" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-wordpress" element={<Navigate to="/geo-center" replace />} />
            <Route path="/geo-media" element={
              <AuthGuard>
                <Layout><GEOCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/media-sessions" element={
              <AuthGuard>
                <Layout><MediaSessionCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-customers" element={
              <AuthGuard>
                <Layout><CustomerManagement /></Layout>
              </AuthGuard>
            } />
            <Route path="/agent-management" element={
              <AuthGuard>
                <Layout><AgentManagement /></Layout>
              </AuthGuard>
            } />
            <Route path="/project-management" element={
              <AuthGuard>
                <Layout><ProjectManagement /></Layout>
              </AuthGuard>
            } />
            <Route path="/geo-customers/intake" element={<Navigate to="/geo-customers" replace />} />
            <Route path="/marketing-tools" element={
              <AuthGuard>
                <Layout><MarketingTools /></Layout>
              </AuthGuard>
            } />
            <Route path="/ai-design-center" element={
              <AuthGuard>
                <Layout><AIDesignCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/prompt-center" element={
              <AuthGuard>
                <Layout><AIPromptCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/keywords" element={<Navigate to="/analytics?tab=keywords" replace />} />
            <Route path="/keyword-center" element={
              <AuthGuard>
                <Layout><KeywordCenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/indexing" element={<Navigate to="/analytics?tab=indexing" replace />} />
            <Route path="/traffic" element={<Navigate to="/analytics?tab=traffic" replace />} />
            <Route path="/competitor" element={<Navigate to="/analytics?tab=competitor" replace />} />
            <Route path="/report-center" element={<Navigate to="/reports" replace />} />
            <Route path="/api-center" element={
              <AuthGuard>
                <Layout><APICenter /></Layout>
              </AuthGuard>
            } />
            <Route path="/ai-config-center" element={<Navigate to="/api-center" replace />} />
            <Route path="/help" element={
              <AuthGuard>
                <Layout><Help /></Layout>
              </AuthGuard>
            } />
            <Route path="/system-settings" element={
              <AuthGuard>
                <Layout><SystemSettings /></Layout>
              </AuthGuard>
            } />
            <Route path="/admin" element={
              <AuthGuard>
                <AdminOnlyRoute><Navigate to="/system-settings" replace /></AdminOnlyRoute>
              </AuthGuard>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
